import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { PORTAL_READ_ONLY_NOTE, PORTAL_SETUP_STEP_QUERY, STEP_STATUS_WORDS, portalSetupStepFromQuery, stepDot, type PortalSetupStepId } from '../../../../lib/kyckConnect/portalSetup';
import type { PortalSetupResponse, PortalSetupStep } from '../../../../types/kyckConnect';
import { ProfileEditor } from '../../../businessProfile/ProfileEditor';
import { PromotionsEditor } from '../../../businessProfile/PromotionsEditor';
import { KcPage } from './KcPage';
import { PortalCallsSection } from './PortalCallsSection';
import { PortalSpeedToLeadForm } from './PortalSpeedToLeadForm';
import { PortalAssistantForm } from './PortalAssistantForm';
import {
  createPortalPromotion,
  deletePortalPromotion,
  fetchPortalKcClubs,
  fetchPortalProfile,
  fetchPortalSetup,
  savePortalProfile,
  updatePortalPromotion,
} from './portalSetupApi';

// THE CLUB'S SETUP (KyckConnect Phase 4): the shared setup steps in the Kyck Design System look. A left list of steps with status
// dots, the editor on the right, one Save per step, a "Shared" tag on the steps a club can edit. ⚠️ The page decides nothing about
// whether anything is set up or editable: the statuses and `editable` flags come from the server, which also refuses every write
// the page does not offer. Without the `kyckconnect_setup` permission the whole page reads and nothing saves.

const DOT_CLASS = {
  done: 'bg-primary',
  // THE ONE VOLT ELEMENT on the page: the step being edited.
  current: 'bg-[#d8ff3e] border border-foreground',
  incomplete: 'border-2 border-foreground bg-transparent',
  off: 'bg-muted border border-border',
} as const;

function ReadOnlyCard({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="kc-card rounded-[18px] border border-border p-4" data-testid={testId}>
      <h3 className="kc-label text-muted-foreground">{title}</h3>
      <div className="mt-1 space-y-1 text-foreground">{children}</div>
    </div>
  );
}

function ServicesStep({ setup }: { setup: PortalSetupResponse }) {
  const s = setup.services;
  return (
    <div className="space-y-3" data-testid="portal-step-services">
      <p className="text-muted-foreground">These are the KyckConnect services running for {setup.accountName}. Kyck switches them on and off, so you see them here but cannot change them.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <ReadOnlyCard title="Lead relay"><p>{s.leadRelay ? 'On' : 'Off'}</p></ReadOnlyCard>
        <ReadOnlyCard title="AI SMS"><p>{s.aiSms.on ? `On${s.aiSms.botName ? ` (${s.aiSms.botName})` : ''}` : 'Off'}</p></ReadOnlyCard>
        <ReadOnlyCard title="Speed to Lead calls"><p>{s.speedToLead ? 'On' : 'Off'}</p></ReadOnlyCard>
      </div>
    </div>
  );
}

function RelayStep({ setup }: { setup: PortalSetupResponse }) {
  const r = setup.relay;
  if (!r) return <p className="text-foreground" data-testid="portal-step-relay">No lead form is connected to your club yet. Talk to Kyck to set it up.</p>;
  return (
    <div className="space-y-3" data-testid="portal-step-relay">
      <p className="text-muted-foreground">Your lead forms send each new lead to your CRM. Kyck manages the connection, so this is a read-only view.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ReadOnlyCard title="Forms connected"><p>{r.routes} connected, {r.live} live</p></ReadOnlyCard>
        <ReadOnlyCard title="Delivered to your CRM, last 30 days">
          <p>{r.delivered} delivered{r.failed > 0 ? `, ${r.failed} failed` : ''}{r.inProgress > 0 ? `, ${r.inProgress} on the way` : ''}</p>
        </ReadOnlyCard>
      </div>
    </div>
  );
}

function ProfileStep({ accountId, editable }: { accountId: string; editable: boolean }) {
  const qc = useQueryClient();
  const key = ['portal-kc', 'profile', accountId];
  const q = useQuery({ queryKey: key, queryFn: () => fetchPortalProfile(accountId), retry: false });
  if (q.isLoading) return <CenteredSpinner />;
  if (q.error || !q.data) return <p className="text-destructive" role="alert">We could not load your business profile just now.</p>;
  return (
    <div data-testid="portal-step-profile">
      <ProfileEditor
        accountName={q.data.accountName}
        profile={q.data.profile}
        timezone={q.data.timezone}
        readOnly={!editable}
        save={async (input) => {
          await savePortalProfile(accountId, input);
          await qc.invalidateQueries({ queryKey: ['portal-kc'] });
        }}
      />
    </div>
  );
}

function AiSmsStep({ setup, editable, step }: { setup: PortalSetupResponse; editable: boolean; step: { editable: boolean } }) {
  const qc = useQueryClient();
  const accountId = setup.accountId;
  const ai = setup.aiSms;
  const q = useQuery({ queryKey: ['portal-kc', 'profile', accountId], queryFn: () => fetchPortalProfile(accountId), retry: false, enabled: Boolean(ai?.canEditPromotions) });
  if (!ai) return <p className="text-foreground" data-testid="portal-step-ai-sms">Your club has no assistant yet. Talk to Kyck to set one up.</p>;
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal-kc'] });
  return (
    <div className="space-y-4" data-testid="portal-step-ai-sms">
      <PortalAssistantForm key={`${accountId}-${ai.settings.assistantName}`} setup={setup} editable={step.editable} />
      <ReadOnlyCard title="Your assistant">
        <p>{ai.botName ?? 'Your assistant'} {ai.textsOnItsOwn ? 'texts leads on its own.' : 'drafts replies that wait for Kyck to approve them.'}</p>
        <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
          {ai.capabilities.map((c) => <li key={c.key}>{c.label}: {c.on ? 'on' : 'off'}</li>)}
        </ul>
        <p className="mt-2 text-sm text-muted-foreground">Kyck sets how your assistant behaves. What it knows about your club comes from your business profile, and the offers below.</p>
      </ReadOnlyCard>
      {ai.canEditPromotions ? (
        q.isLoading ? <CenteredSpinner /> : q.data ? (
          <PromotionsEditor
            promotions={q.data.promotions}
            readOnly={!editable}
            create={async (input) => { await createPortalPromotion(accountId, input); await refresh(); }}
            update={async (id, input) => { await updatePortalPromotion(accountId, id, input); await refresh(); }}
            remove={async (id) => { await deletePortalPromotion(accountId, id); await refresh(); }}
          />
        ) : <p className="text-destructive" role="alert">We could not load your offers just now.</p>
      ) : (
        <p className="text-muted-foreground" data-testid="offers-off">Offers are off for your assistant. Talk to Kyck to turn this on.</p>
      )}
    </div>
  );
}

function StepBody({ step, setup }: { step: PortalSetupStep; setup: PortalSetupResponse }) {
  if (!step.visible) return <p className="text-foreground" data-testid="portal-step-off">{step.hiddenMessage}</p>;
  switch (step.id) {
    case 'services': return <ServicesStep setup={setup} />;
    case 'profile': return <ProfileStep accountId={setup.accountId} editable={step.editable} />;
    case 'relay': return <RelayStep setup={setup} />;
    case 'aiSms': return <AiSmsStep setup={setup} editable={step.editable} step={step} />;
    case 'speedToLead':
      return (
        <div className="space-y-6">
          <PortalSpeedToLeadForm accountId={setup.accountId} editable={step.editable} />
          <PortalCallsSection accountId={setup.accountId} />
        </div>
      );
  }
}

export function PortalSetupPage() {
  const [params, setParams] = useSearchParams();
  const clubs = useQuery({ queryKey: ['portal-kc', 'clubs'], queryFn: fetchPortalKcClubs, retry: false });
  const clubId = params.get('club') ?? clubs.data?.clubs[0]?.accountId ?? null;
  const setupQuery = useQuery({ queryKey: ['portal-kc', 'setup', clubId], queryFn: () => fetchPortalSetup(clubId as string), enabled: Boolean(clubId), retry: false });
  const requested = portalSetupStepFromQuery(params.get('step'));
  const setup = setupQuery.data;
  const open: PortalSetupStepId = requested ?? setup?.steps.find((s) => s.visible && s.status === 'incomplete')?.id ?? 'profile';

  // A club id that is not the viewer's resolves to the first club instead of an error page.
  useEffect(() => {
    if (clubs.data && params.get('club') && !clubs.data.clubs.some((c) => c.accountId === params.get('club'))) {
      const next = new URLSearchParams(params);
      next.delete('club');
      setParams(next, { replace: true });
    }
  }, [clubs.data, params, setParams]);

  const pick = (patch: { club?: string; step?: PortalSetupStepId }) => {
    const next = new URLSearchParams(params);
    if (patch.club) next.set('club', patch.club);
    if (patch.step) next.set('step', PORTAL_SETUP_STEP_QUERY[patch.step]);
    setParams(next, { replace: true });
  };

  const step = setup?.steps.find((s) => s.id === open) ?? null;
  return (
    <KcPage title="Setup" intro="How KyckConnect is set up for your club. Steps tagged Shared are yours to change." testId="portal-setup-theme">
      {clubs.isLoading && <CenteredSpinner />}
      {clubs.error && <p className="text-destructive" role="alert">We could not load your clubs just now.</p>}
      {clubs.data && clubs.data.clubs.length === 0 && <p className="text-muted-foreground">No clubs to show.</p>}
      {clubs.data && clubs.data.clubs.length > 1 && (
        <label className="mb-4 block text-sm text-foreground">
          <span className="kc-label mb-1 block text-muted-foreground">Club</span>
          <select className="kc-field rounded border border-border bg-background px-2 py-1 text-foreground" value={clubId ?? ''} onChange={(e) => pick({ club: e.target.value })} aria-label="Club">
            {clubs.data.clubs.map((c) => <option key={c.accountId} value={c.accountId}>{c.name}</option>)}
          </select>
        </label>
      )}
      {setupQuery.isLoading && <CenteredSpinner />}
      {setupQuery.error && <p className="text-destructive" role="alert">We could not load your setup just now.</p>}
      {setup && (
        <>
          {!setup.canEdit && <p className="kc-card mb-4 rounded-[18px] border border-border p-3 text-foreground" data-testid="read-only-banner">{PORTAL_READ_ONLY_NOTE}</p>}
          <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
            <ol className="space-y-1" aria-label="Setup steps" data-testid="portal-step-list">
              {setup.steps.map((s) => {
                const dot = stepDot(s.status, s.id === open);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => pick({ step: s.id })}
                      aria-current={s.id === open ? 'step' : undefined}
                      className={`kc-row flex w-full items-center gap-3 rounded-[18px] border px-3 py-2 text-left ${s.id === open ? 'border-foreground' : 'border-border'}`}
                      data-testid={`portal-step-${s.id}`}
                      data-dot={dot}
                    >
                      <span className={`h-3 w-3 shrink-0 rounded-full ${DOT_CLASS[dot]}`} aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-foreground">{s.label}</span>
                        <span className="block text-sm text-muted-foreground">{STEP_STATUS_WORDS[s.status]}</span>
                      </span>
                      {s.shared && <span className="kc-pill rounded-full border border-border px-2 py-0.5 text-muted-foreground" data-testid={`shared-${s.id}`}>Shared</span>}
                    </button>
                  </li>
                );
              })}
            </ol>
            <section className="kc-card min-w-0 rounded-[18px] border border-border p-4" aria-label={step?.label ?? 'Step'}>
              {step && (
                <>
                  <h2 className="kc-display">{step.label}</h2>
                  <p className="mb-3 text-muted-foreground">{step.visible ? step.detail : ''}</p>
                  <StepBody key={`${setup.accountId}:${step.id}`} step={step} setup={setup} />
                </>
              )}
            </section>
          </div>
        </>
      )}
    </KcPage>
  );
}
